import { Product } from '@gozoki/api-types';

import { ProductWithCount } from '../../pages/dashboard/DashboardPage';

/**
 * Utilitaire pour agréger des articles en produits avec comptage
 * Évite la duplication de logique et les problèmes de race condition
 */
export const aggregateProducts = (
    articles: Array<{ product: Product; soldPrice?: number | null }>,
    priceField: 'sellPrice' | 'price' = 'sellPrice'
): ProductWithCount[] => {
    const products = new Map<string, { product: Product; count: number; ca: number }>();

    articles.forEach((article) => {
        const { product } = article;
        if (!product) return;
        const ca = article.soldPrice ?? product[priceField];

        if (products.has(product.reference)) {
            const existing = products.get(product.reference)!;
            existing.count += 1;
            existing.ca += ca;
        } else {
            products.set(product.reference, {
                product,
                count: 1,
                ca,
            });
        }
    });

    return Array.from(products.values())
        .sort((a, b) => b.count - a.count)
        .map((p) => {
            const { product } = p;
            return {
                ...product,
                count: p.count,
                ca: p.ca,
            };
        });
};

/**
 * Utilitaire pour agréger des produits avec comptage global
 * Utilisé pour les statistiques globales
 */
export const aggregateGlobalProducts = (
    inventoryStatsArray: Array<{
        delivered: ProductWithCount[];
        destroyed: ProductWithCount[];
        defective: ProductWithCount[];
        stolen: ProductWithCount[];
        errored: ProductWithCount[];
        sold: ProductWithCount[];
    }>,
    category: 'delivered' | 'destroyed' | 'defective' | 'stolen' | 'sold' | 'errored'
): ProductWithCount[] => {
    const aggregatedProducts = new Map<string, ProductWithCount>();

    inventoryStatsArray.forEach((inventoryStats) => {
        inventoryStats[category].forEach((product) => {
            if (aggregatedProducts.has(product.reference)) {
                const existing = aggregatedProducts.get(product.reference)!;
                existing.count += product.count;
                existing.ca += product.ca;
            } else {
                aggregatedProducts.set(product.reference, product);
            }
        });
    });

    return Array.from(aggregatedProducts.values()).sort((a, b) => b.count - a.count);
};
